import { initializeApp } from 'firebase/app'
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  connectAuthEmulator,
} from 'firebase/auth'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
// Firestore is initialized lazily in the ops chunk (src/ops/lib/data.js) so the
// public estimate bundle doesn't ship the Firestore SDK it never uses.

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// True only when a real project is configured. When false, the app still runs
// fully (see lib/api.js mock fallbacks) so the flow is demoable without a
// backend. We must NOT call getAuth/getFunctions/getStorage in that case —
// getAuth() throws auth/invalid-api-key on a missing key and would blank the app.
export const isBackendConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId)

const region = import.meta.env.VITE_FUNCTIONS_REGION || 'us-central1'

let _app = null
let _auth = null
let _functions = null
let _storage = null

// Anonymous auth gives every visitor a uid so Storage rules can scope uploads
// to scans/{uid}/ and the vision callable gets an auth context. Invisible to the
// user.
//
// IMPORTANT: signing in must NEVER be able to hang the UI. If Anonymous Auth is
// disabled in the Firebase console (Authentication → Sign-in method → Anonymous)
// or the network drops, we resolve with `null` instead of leaving callers
// awaiting forever. The app then degrades gracefully — the manual estimate still
// works, and the photo path falls back to manual entry instead of spinning.
let resolveAuthReady
export const authReady = new Promise((resolve) => {
  resolveAuthReady = resolve
})

// Hard ceiling on how long we'll wait for the anonymous handshake (ms).
const AUTH_TIMEOUT_MS = 8000

if (isBackendConfigured) {
  _app = initializeApp(firebaseConfig)
  _auth = getAuth(_app)
  _functions = getFunctions(_app, region)
  _storage = getStorage(_app)

  if (import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectAuthEmulator(_auth, 'http://localhost:9099', { disableWarnings: true })
    connectFunctionsEmulator(_functions, 'localhost', 5001)
    connectStorageEmulator(_storage, 'localhost', 9199)
  }

  onAuthStateChanged(
    _auth,
    (user) => {
      if (user) resolveAuthReady(user.uid)
    },
    (err) => {
      // Listener error — unblock callers rather than hang.
      console.error('Auth state error:', err?.code || err)
      resolveAuthReady(null)
    },
  )
} else {
  resolveAuthReady('mock-uid')
}

export const app = _app
export const auth = _auth
export const functions = _functions
export const storage = _storage

// Resolves to a uid string when signed in, or `null` when auth is unavailable.
// NEVER rejects and NEVER hangs — every caller can always make progress.
let signInStarted = false
export function ensureSignedIn() {
  if (!isBackendConfigured) return authReady // resolves to 'mock-uid'
  if (_auth?.currentUser) return Promise.resolve(_auth.currentUser.uid)

  if (!signInStarted) {
    signInStarted = true
    signInAnonymously(_auth).catch((e) => {
      // Most common cause: the Anonymous provider isn't enabled in the Firebase
      // console. Resolve null so the flow degrades instead of hanging forever.
      console.error('Anonymous sign-in failed — is Anonymous Auth enabled?', e?.code || e)
      resolveAuthReady(null)
    })
  }

  // Safety net: never wait longer than AUTH_TIMEOUT_MS on the handshake.
  return Promise.race([
    authReady,
    new Promise((resolve) => {
      setTimeout(() => resolve(null), AUTH_TIMEOUT_MS)
    }),
  ])
}
